import React, { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { COLORS } from '@/lib/colors';

interface Props {
  universities: Array<{
    national_rank: number;
    university: string;
  }>;
}

export function RankingsDistributionChart({ universities }: Props) {
  // Create histogram data with 50-rank intervals
  const histogramData = useMemo(() => {
    const binSize = 50; // Universities per bin
    const bins: { range: string; count: number; universities: string[]; hasDePaul: boolean }[] = [];
    
    // Sort universities by rank
    const sortedUniversities = [...universities].sort(
      (a, b) => a.national_rank - b.national_rank
    );

    // Create bins with 50-rank intervals
    for (let i = 0; i < 400; i += binSize) {
      const rangeStart = i + 1;
      const rangeEnd = i + binSize;
      const universitiesInRange = sortedUniversities.filter(
        u => u.national_rank >= rangeStart && u.national_rank <= rangeEnd
      );
      
      bins.push({
        range: `${rangeStart}-${rangeEnd}`,
        count: universitiesInRange.length,
        universities: universitiesInRange.map(u => u.university),
        hasDePaul: universitiesInRange.some(u => u.university === 'DePaul University')
      });
    }

    return bins;
  }, [universities]);

  const options = {
    chart: {
      type: 'bar',
      fontFamily: 'inherit',
      toolbar: {
        show: false
      }
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        dataLabels: {
          position: 'top'
        },
        distributed: true
      }
    },
    colors: histogramData.map(bin => bin.hasDePaul ? COLORS.accent.orange : COLORS.primary.darkBlue),
    dataLabels: {
      enabled: true,
      formatter: function (val: number) {
        return val;
      },
      offsetY: -20,
      style: {
        fontSize: '12px',
        colors: [COLORS.text.primary]
      }
    },
    xaxis: {
      categories: histogramData.map(bin => bin.range),
      title: {
        text: 'Rank Range',
        style: {
          fontSize: '12px',
          fontWeight: 500,
          color: COLORS.text.primary
        }
      },
      labels: {
        rotate: -45,
        rotateAlways: true,
        style: {
          fontSize: '11px',
          colors: COLORS.text.primary
        }
      }
    },
    yaxis: {
      title: {
        text: 'Number of Universities',
        style: {
          fontSize: '12px',
          fontWeight: 500,
          color: COLORS.text.primary
        }
      },
      max: Math.ceil(Math.max(...histogramData.map(bin => bin.count)) * 1.2) // Add 20% padding
    },
    tooltip: {
      shared: true,
      intersect: false,
      custom: function({ series, seriesIndex, dataPointIndex }: any) {
        const data = histogramData[dataPointIndex];
        const depaulHighlight = data.universities.map(uni => 
          uni === 'DePaul University' 
            ? `<div class="text-sm font-bold" style="color: ${COLORS.accent.orange}">${uni}</div>`
            : `<div class="text-sm" style="color: ${COLORS.text.secondary}">${uni}</div>`
        ).join('');
        
        return `
          <div class="p-4 bg-white rounded-lg shadow-lg border">
            <div class="font-medium mb-2">Rank Range: ${data.range}</div>
            <div style="color: ${COLORS.text.secondary}" class="mb-2">Number of Universities: ${data.count}</div>
            <div class="mt-2">
              <div class="font-medium mb-1">Universities:</div>
              <div class="max-h-32 overflow-y-auto">
                ${depaulHighlight}
              </div>
            </div>
          </div>
        `;
      }
    },
    grid: {
      borderColor: COLORS.border.light,
      strokeDashArray: 3
    }
  };

  const series = [{
    name: 'Universities',
    data: histogramData.map(bin => bin.count)
  }];

  return (
    <div className="w-full h-[400px]">
      <ReactApexChart
        options={options}
        series={series}
        type="bar"
        height={400}
      />
    </div>
  );
}
